import { ActionFunctionArgs, LoaderFunctionArgs } from "react-router-dom";
import { loadCookiesData, INTENTS } from "~/utils";
import type { FormattedResourceAccessRight } from "~/types";
import * as APITypes from "~/API";
import { getAuth } from "firebase/auth";
import { getReadyAuthUser } from "~/utils/auth/waitForAuthReady";
import { apiClient, InstanceAccessTabLoaderResult } from "~/services/apiClient";

/**
 * Loader for InstanceAccessTab
 * Fetches all instance access rights for a specific member
 */
export async function InstanceAccessTabLoader({
  params,
}: LoaderFunctionArgs): Promise<InstanceAccessTabLoaderType> {
  const auth = getAuth();
  const user = await getReadyAuthUser();

  const authAccessInfo = await loadCookiesData(user?.uid || "");

  const memberID = params.userID || "";
  const organizationID = params.organizationID || "";

  const result = await apiClient.loader<InstanceAccessTabLoaderResult>(
    "instance-access-tab",
    { id: memberID }
  );

  if (!result.success || !result.data) {
    console.error("Failed to load instance access tab:", result.error);
    throw new Error(
      result.error ?? "Failed to load instance access tab"
    );
  }

  const data = result.data;
  const memberRole = data.memberRole;

  const isAdminOrSuperAdmin =
    memberRole === APITypes.ManagerRolesType.SuperAdmin ||
    memberRole === APITypes.ManagerRolesType.Admin;

  const currentUserRole = authAccessInfo.data?.role;
  const canUpdateMemberRights =
    currentUserRole === APITypes.ManagerRolesType.SuperAdmin ||
    currentUserRole === APITypes.ManagerRolesType.Admin;

  return {
    accessRights: data.accessRights as FormattedResourceAccessRight[],
    memberID,
    organizationID,
    memberRole,
    isAdminOrSuperAdmin,
    canUpdateMemberRights,
  };
}

export type InstanceAccessTabLoaderType = {
  accessRights: FormattedResourceAccessRight[];
  memberID: string;
  organizationID: string;
  memberRole?: APITypes.ManagerRolesType | null;
  isAdminOrSuperAdmin: boolean;
  canUpdateMemberRights: boolean;
};

export const InstanceAccessTabAction = async ({
  request,
  params,
}: ActionFunctionArgs) => {
  const auth = getAuth();
  const user = await getReadyAuthUser();
  const formData = await request.formData();
  const intent = formData.get("intent");
  const memberID = params.userID || "";
  const organizationID = params.organizationID || "";

  switch (intent) {
    case INTENTS._add_instance_access: {
      const raw = JSON.parse(formData.get("body") as string) as {
        resourceType?: APITypes.EntityType;
        resourceID?: string;
        readAccessStatus?: APITypes.ResourceAccessRightStatus;
        writeAccessStatus?: APITypes.ResourceAccessRightStatus;
        existingRecordID?: string;
        existingRecordVersion?: number;
        applyReadToChildren?: boolean;
        applyWriteToChildren?: boolean;
      };
      const result = await apiClient.sendIntent<{
        success: boolean;
        childrenGranted?: number;
        childrenFailed?: number;
        totalChildren?: number;
        childrenError?: boolean;
      }>(INTENTS._add_instance_access, {
        ...raw,
        memberID,
        organizationID,
      });
      return result;
    }

    case INTENTS._edit_instance_access: {
      const raw = JSON.parse(formData.get("body") as string) as {
        id?: string;
        readAccessStatus?: APITypes.ResourceAccessRightStatus;
        writeAccessStatus?: APITypes.ResourceAccessRightStatus;
        _version?: number;
      };
      if (!raw.id || raw.readAccessStatus === undefined || raw.writeAccessStatus === undefined || raw._version === undefined) {
        return null;
      }
      const result = await apiClient.sendIntent<{ success: boolean; data: unknown }>(
        INTENTS._edit_instance_access,
        { ...raw, organizationID }
      );
      return result;
    }

    case INTENTS._remove_instance_access: {
      const body = formData.get("body") as string;
      const raw = body
        ? (JSON.parse(body) as { resourceID?: string; _version?: number })
        : {};
      if (!raw.resourceID || raw._version === undefined) {
        return null;
      }
      const result = await apiClient.sendIntent<{ success: boolean }>(
        INTENTS._remove_instance_access,
        {
          memberID,
          organizationID,
          resourceID: raw.resourceID,
          _version: raw._version,
        }
      );
      return result;
    }

    default:
      return null;
  }
};
